/**
 * @unscoped — ApprovalPage 의 i18n — Cycle 50.
 *
 * cycle 26 drift-i18n / cycle 37 admin-i18n / cycle 41 base-i18n 통합 패턴 일관.
 * lang state 는 base-i18n 의 globalThis 단일 source — 본 모듈은 MESSAGES dict + tApproval helper.
 *
 * cycle 50 scope: ApprovalPage.tsx + ApprovalSidebar.tsx 의 32 strings.
 *   ApprovalCompose.tsx / ApprovalDetail.tsx 의 22 strings 는 cycle 51 backlog.
 *
 * ⚠ **2026-08-31 — 트리(메뉴) 이름은 더 이상 여기서 오지 않는다.**
 *   결재에 셸(`ApprShell`)이 서면서 메뉴는 `lib/appr-menu.ts` 의 한글 글자로 산다 —
 *   인사(`hr-menu`)·근태(`att-menu`) 트리와 같은 방식이다. 그렇게 한 이유는
 *   **「메뉴 이름 = 화면 제목」** 규율 때문이다: 화면들의 `<h1>` 이 한글 글자라(`양식 · 서식`
 *   `결재선` …) 메뉴만 번역하면 en·ja 에서 둘이 갈린다. 화면 제목까지 번역하는 판이 오면
 *   그때 메뉴도 함께 옮긴다.
 *   ★ 아래 `approval.group.*` · `approval.item.*` 는 그래서 **지금 쓰는 곳이 없다.**
 *     지우지 않는 것은 그 판이 올 때 문구를 다시 짓지 않으려는 것이다(검사도 이 값을 쓴다).
 *   ★ 지금 실제로 쓰는 것은 문서함 글자들이다 — `approval.loading` · `approval.empty` ·
 *     `approval.urgent_badge` · `approval.steps_suffix` · `approval.list.aria_label` ·
 *     `approval.compose.*`(작성 창).
 *
 * 정책 (cycle 46/49 학습 적용):
 *   - fallback chain: lang → en → FALLBACK_LANG (ko) — partial 정의 안전망
 *   - drift-i18n.ts 와 동일 t() 패턴
 */

import {
  getSharedLang, setSharedLang, subscribeSharedLang,
  FALLBACK_LANG, type Lang,
} from './base-i18n';

const MESSAGES: Record<string, Record<Lang, string>> = {
  // ----- ApprovalSidebar group label (4 그룹) -----
  // 결재 홈 (2026-08-28 · 자리 재정의 판 6) — 들어오면 여기에 떨어진다.
  'approval.group.home': { ko: '현황', en: 'Overview', ja: '現況' },
  'approval.item.home': { ko: '결재 홈', en: 'Approval home', ja: '決裁ホーム' },
  // 결재 관리 (2026-08-28 · 판 6) — 회사가 정하는 결재 기준. 인사에서 옮겨 왔다.
  'approval.group.admin': { ko: '관리', en: 'Administration', ja: '管理' },
  /**
   * ★ 「문서 종류」 → **「양식 · 서식」** (2026-08-31 · 시안 맞춤 7/9).
   *   화면이 다루는 것이 종류 이름만이 아니라 **그 종류의 칸(서식)** 까지이므로,
   *   시안의 이름을 그대로 쓴다. 화면 `<h1>` 도 같이 바뀐다(`hr-menu-titles.test.ts` 가 본다).
   */
  'approval.item.admin_doctype': { ko: '양식 · 서식', en: 'Forms & fields', ja: '様式・書式' },
  /**
   * 기준 설정 (2026-08-29 · 판 10) — 계정과목. 근태·인사의 「기준 설정」과 **같은 이름**이다:
   * 도메인마다 목록이 다를 뿐 하는 일이 같아, 이름을 달리하면 셋을 서로 다른 것으로 읽는다.
   */
  'approval.item.admin_stats': { ko: '결재 현황', en: 'Approval overview', ja: '決裁状況' },
  'approval.item.admin_codes': { ko: '기준 설정', en: 'Reference data', ja: '基準設定' },
  'approval.group.draft': { ko: '기안', en: 'Drafts', ja: '起案' },
  'approval.group.approve': { ko: '결재', en: 'Approvals', ja: '決裁' },
  'approval.group.public': { ko: '공개', en: 'Public', ja: '公開' },
  /* ── 시안 F1 의 묶음 셋 (2026-08-31) ─────────────────────────────────
     종전 여섯 묶음(현황·기안·결재·공개·관리·개인설정)은 **손이 가는 순서**가 아니라
     낱말로 나뉘어 있었다. 시안은 «내가 결재하는 일 / 회사가 정하는 일 / 내 설정» 셋이다. */
  'approval.group.work': { ko: '결재하기', en: 'Approvals', ja: '決裁する' },
  'approval.group.manage': { ko: '관리 · 담당자', en: 'Administration', ja: '管理・担当者' },
  'approval.group.personal': { ko: '개인 설정', en: 'Personal', ja: '個人設定' },
  // 부제 — 「이 화면이 무엇을 정하는가」.
  'approval.hint.admin_stats': { ko: '양식별 · 사람별 병목', en: 'Bottlenecks by form and person', ja: '様式別・人別ボトルネック' },
  'approval.hint.admin_doctype': { ko: '종류 · 칸 · 전결', en: 'Types, fields, delegation limits', ja: '種類・欄・専決' },
  /**
   * ⚠ **2026-09-04(판 P6) — 「사다리」를 걷어냈다.** 판 P1 이 조직 사다리를 폐기했는데
   *    이 부제만 남아 **화면에 없는 것을 있다고 말하고 있었다**(세 언어 모두).
   */
  'approval.hint.admin_line': { ko: '기본 결재선 · 즐겨찾기', en: 'Default lines and favorites', ja: '既定の決裁線・お気に入り' },
  'approval.hint.admin_codes': { ko: '계정과목 등 기준값', en: 'Reference values', ja: '勘定科目など基準値' },
  'approval.hint.settings_proxy': { ko: '기간 · 대상', en: 'Period and target', ja: '期間・対象' },
  'approval.item.settings_signature': { ko: '서명 · 도장', en: 'Signature & stamp', ja: '署名・印' },
  'approval.hint.settings_signature': { ko: '결재에 찍히는 내 도장', en: 'The stamp on your approvals', ja: '決裁に押される印' },
  'approval.group.settings': { ko: '개인설정', en: 'Personal settings', ja: '個人設定' },

  // ----- ApprovalSidebar item label (11 키) — ApprovalSidebarItemId enum 와 1:1 -----
  'approval.item.draft_write': { ko: '기안문 작성', en: 'Create draft', ja: '起案作成' },
  'approval.item.draft_temp': { ko: '임시저장함', en: 'Temporary saves', ja: '一時保存箱' },
  'approval.item.draft_box': { ko: '기안문서함', en: 'My drafts', ja: '起案文書箱' },
  'approval.item.approve_all': { ko: '전체문서함', en: 'All documents', ja: '全文書箱' },
  'approval.item.approve_pending': { ko: '승인대기함', en: 'Pending approval', ja: '承認待ち箱' },
  'approval.item.approve_upcoming': { ko: '승인예정함', en: 'Upcoming approvals', ja: '承認予定箱' },
  'approval.item.approve_done': { ko: '승인처리함', en: 'Completed approvals', ja: '承認処理箱' },
  'approval.item.approve_shared': { ko: '공유문서함', en: 'Shared documents', ja: '共有文書箱' },
  'approval.item.public_box': { ko: '공개문서함', en: 'Public documents', ja: '公開文書箱' },
  /**
   * ★ 메뉴 이름 = 화면 제목 (2026-08-28 검사가 잡았다) — 메뉴는 「대리결재관리」인데 열면
   *   「결재 위임」이 떴다. 이 기능은 어디서나 **위임**이라 부른다(`DelegatePage` ·
   *   `/approval/delegates` · 「내가 맡긴 위임」) — 메뉴 쪽을 맞춘다.
   */
  'approval.item.settings_proxy': { ko: '결재 위임', en: 'Delegation', ja: '代理決裁管理' },
  /*
   * ★ 2026-08-31 — 이름을 **「결재선」**으로 줄였다(시안 F1). 부제가 무엇을 다루는지 이미
   *   말하고, 묶음 이름이 「관리 · 담당자」라 «관리»도 겹쳤다.
   *   ⚠ 이 값은 **화면 제목이기도 하다**(`SIDEBAR_TITLE_KEY`) — 메뉴와 제목이 함께 바뀐다.
   */
  'approval.item.settings_favline': { ko: '결재선', en: 'Approval lines', ja: '決裁線' },

  // ----- ApprovalPage 메인 영역 -----
  'approval.nav.aria_label': { ko: '결재 메뉴', en: 'Approval menu', ja: '決裁メニュー' },
  'approval.list.aria_label': { ko: '결재 목록', en: 'Approval list', ja: '決裁リスト' },
  'approval.compose_btn': { ko: '기안하기', en: 'New draft', ja: '起案する' },
  'approval.loading': { ko: '불러오는 중…', en: 'Loading…', ja: '読み込み中...' },
  'approval.empty': { ko: '표시할 결재가 없습니다.', en: 'No approvals to display.', ja: '表示する決裁がありません。' },
  'approval.urgent_badge': { ko: '긴급', en: 'Urgent', ja: '緊急' },
  'approval.steps_suffix': { ko: '단계', en: 'steps', ja: 'ステップ' },

  // ----- ApprovalCompose (cycle 50 잔여 마감) -----
  'approval.compose.heading': { ko: '결재 기안', en: 'New approval draft', ja: '決裁起案' },
  'approval.compose.close_aria': { ko: '닫기', en: 'Close', ja: '閉じる' },
  'approval.compose.field.title': { ko: '제목', en: 'Title', ja: 'タイトル' },
  'approval.compose.field.type': { ko: '유형', en: 'Type', ja: '種別' },
  'approval.compose.field.content': { ko: '본문', en: 'Content', ja: '本文' },
  'approval.compose.field.approvers': {
    ko: '결재선 (사용자 ID, 콤마 구분)',
    en: 'Approvers (user IDs, comma-separated)',
    ja: '決裁ライン(ユーザーID、カンマ区切り)',
  },
  'approval.compose.field.approvers_hint': { ko: '예: 11,12,13', en: 'e.g., 11,12,13', ja: '例: 11,12,13' },
  'approval.compose.field.urgent': { ko: '긴급 결재', en: 'Urgent approval', ja: '緊急決裁' },
  'approval.compose.btn.cancel': { ko: '취소', en: 'Cancel', ja: 'キャンセル' },
  'approval.compose.btn.submit_idle': { ko: '기안하기', en: 'Submit draft', ja: '起案する' },
  'approval.compose.btn.submit_loading': { ko: '제출 중…', en: 'Submitting…', ja: '送信中...' },
  'approval.compose.err.approvers_required': {
    ko: '결재선 (approverIds) 을 1명 이상 입력해주세요.',
    en: 'Please add at least one approver (approverIds).',
    ja: '決裁ライン(approverIds)を1名以上入力してください。',
  },
  // ----- ApprovalCompose 유형 옵션 (TYPE_OPTIONS 6건) -----
  'approval.type.LEAVE': { ko: '연차/휴가', en: 'Leave / vacation', ja: '年休/休暇' },
  'approval.type.BUSINESS_TRIP': { ko: '출장', en: 'Business trip', ja: '出張' },
  'approval.type.OVERTIME': { ko: '시간외 근무', en: 'Overtime', ja: '時間外勤務' },
  'approval.type.EARLY_LEAVE': { ko: '조퇴', en: 'Early leave', ja: '早退' },
  'approval.type.EXPENSE': { ko: '경비 집행', en: 'Expense', ja: '経費執行' },
  'approval.type.OTHER': { ko: '기타', en: 'Other', ja: 'その他' },
  // ★ 운영에서 실제로 쓰이는데 목록에 없었다(2026-08-31 · 결재 현황에서 코드가 그대로 보였다).
  'approval.type.OUTSIDE_WORK': { ko: '외근', en: 'Outside work', ja: '外勤' },
  // ★ 같은 이유로 빠져 있던 것 하나 더 — 운영 여덟 중 이것만 없었다(2026-09-01).
  'approval.type.ATTENDANCE_FIX': { ko: '근태 정정', en: 'Attendance correction', ja: '勤怠訂正' },
  // ★ 업무 보고 셋 (2026-09-01 · 서식 채우기) — 종류를 새로 열면서 이름도 함께 넣는다.
  'approval.type.REPORT_DAILY': { ko: '업무 일일 보고', en: 'Daily report', ja: '日報' },
  'approval.type.REPORT_WEEKLY': { ko: '업무 주간 보고', en: 'Weekly report', ja: '週報' },
  'approval.type.REPORT_MONTHLY': { ko: '업무 월간 보고', en: 'Monthly report', ja: '月報' },

  // ----- ApprovalDetail (cycle 50 잔여 마감) -----
  'approval.detail.body_aria': { ko: '본문', en: 'Content', ja: '本文' },
  'approval.detail.body_heading': { ko: '본문', en: 'Content', ja: '本文' },
  'approval.detail.actions_aria': { ko: '결재 액션', en: 'Approval actions', ja: '決裁アクション' },
  'approval.detail.comment_placeholder': { ko: '의견 (선택)', en: 'Comment (optional)', ja: 'コメント(任意)' },
  'approval.detail.btn_approve': { ko: '승인', en: 'Approve', ja: '承認' },
  'approval.detail.btn_reject': { ko: '반려', en: 'Reject', ja: '却下' },
  'approval.detail.btn_recall': { ko: '회수', en: 'Recall', ja: '回収' },
  'approval.detail.progress_prefix': { ko: '진행', en: 'Progress', ja: '進行' },
  // ApprovalDetail 의 line status badge (LINE_STATUS_LABEL Record)
  'approval.line_status.WAITING': { ko: '대기', en: 'Waiting', ja: '待機' },
  'approval.line_status.APPROVED': { ko: '승인', en: 'Approved', ja: '承認' },
  'approval.line_status.REJECTED': { ko: '반려', en: 'Rejected', ja: '却下' },
  'approval.line_status.SKIPPED': { ko: '건너뜀', en: 'Skipped', ja: 'スキップ' },
};

export type ApprovalI18nKey = keyof typeof MESSAGES;

/**
 * Cycle 50 — ApprovalPage i18n lookup.
 * cycle 46 학습 [[antipattern-i18n-third-lang-cascading-fallback-without-explicit-fallback-chain]]
 * 의 phase 5 fallback chain (lang → en → ko) 적용.
 */
export function tApproval(key: ApprovalI18nKey): string {
  const m = MESSAGES[key];
  if (!m) return key;
  return m[getSharedLang()] ?? m.en ?? m[FALLBACK_LANG] ?? key;
}

// base-i18n thin wrapper — drift-i18n / admin-i18n 패턴 일관 (cycle 41).
export function getApprovalLang(): Lang { return getSharedLang(); }
export function setApprovalLang(value: Lang): void { setSharedLang(value); }
export function subscribeApprovalLang(fn: () => void): () => void {
  return subscribeSharedLang(fn);
}
